<template>
	<UPage
		:ui="{
			root: 'w-full my-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-2 relative isolate overflow-hidden'
		}"
	>
		<div class="max-w-container mx-auto w-full justify-items-center">
			<section class="w-full sm:max-w-4xl">
				<h2
					v-if="showDefaultHeader"
					class="text-4xl sm:text-5xl flex leading-13 p-4 sm:leading-14 pb-8 font-semibold flex-col tracking-tight text-center"
				>
					<span class="text-primary">
						{{ t('get-fiber.header.default.headline-1') }}
					</span>
					<span class="text-default">
						{{ t('get-fiber.header.default.headline-2') }}
					</span>
				</h2>
				<p
					v-if="showDefaultHeader"
					class="text-lg sm:text-xl text-center pb-6 sm:pb-8 font-normal text-default max-w-2xl mx-auto text-pretty"
				>
					{{ t('get-fiber.header.default.subheadline') }}
				</p>
			</section>
			<section class="w-full sm:max-w-4xl">
				<SignupAddressChecker
					v-if="showAddressCheck"
					id="get-fiber-address-checker"
					:button-text="$t('address-checker.button-text')"
					class="max-w-3xl mx-auto"
				/>

				<SignupChoosePlan
					v-if="showChoosePlan"
					:internet-available="internetAvailable"
					:under-construction="underConstruction"
					:internet-unavailable="internetUnavailable"
					:moving-in="movingIn"
				/>
				<SignupAlreadyRegistered
					v-if="showAlreadyRegistered && !movingIn"
				/>
				<SignupComplete
					v-if="showComplete"
					:thank-you-text="$t('get-fiber.complete-page.h2')"
				/>
			</section>
		</div>
	</UPage>
</template>

<script setup lang="ts">
import { useAddressLocalStorage } from '@/composables/useLocalStorage'
import {
	BASE_URL, COMPANY_NAME
} from '@/constants/index.js'
import {
	ALREADY_REGISTERED,
	AVAILABLE,
	COMPLETE,
	UNAVAILABLE,
	UNDER_CONSTRUCTION
} from '@/constants/signup-queries'
import { trackSignupStatus } from '@/helpers/internetSignup/trackSignupStatus'
import { useI18n } from 'vue-i18n'
import type { LocationQueryValue } from 'vue-router'
import { useRoute } from 'vue-router'

const { t } = useI18n({
	useScope: 'global'
})
const localePath = useLocalePath()

const route = useRoute()

useHead({
	title: t('get-fiber.meta-title'),
	meta: [
		{
			name: 'description',
			content: t('get-fiber.meta-description', { COMPANY_NAME })
		},
		{
			name: 'url',
			content: `${BASE_URL}/get-fiber`
		}
	]
})

const { cachedAddressResponse } = useAddressLocalStorage()

const address = ref(cachedAddressResponse?.value?.address || '')

const fiberStatus = computed<LocationQueryValue | LocationQueryValue[] | undefined>(() => {
	return route.query?.fiber
})
const movingIn = computed(() => {
	return route.query?.moving_in === 'true'
})
const showAddressCheck = computed(() => {
	return !fiberStatus.value || fiberStatus.value === undefined
})
const internetAvailable = computed(() => {
	return fiberStatus.value === AVAILABLE
})
const underConstruction = computed(() => {
	return fiberStatus.value === UNDER_CONSTRUCTION
})
const internetUnavailable = computed(() => {
	return fiberStatus.value === UNAVAILABLE
})
const showChoosePlan = computed(() => {
	return internetAvailable.value
		|| underConstruction.value || internetUnavailable.value || (showAlreadyRegistered.value && movingIn.value)
})
const showAlreadyRegistered = computed(() => {
	return fiberStatus.value === ALREADY_REGISTERED
})
const showComplete = computed(() => {
	return fiberStatus.value === COMPLETE
})

const showDefaultHeader = computed(() => {
	return showAddressCheck.value && !showChoosePlan.value && !showAlreadyRegistered.value && !showComplete.value
})

const handleTrackSignupStatus = () => {
	if (!fiberStatus.value) {
		return
	}
	trackSignupStatus(fiberStatus.value, address.value)
}

onUpdated(() => {
	handleTrackSignupStatus()
})

onMounted(() => {
	// If the address is already cached, we can use it directly
	cachedAddressResponse.value = useAddressLocalStorage().cachedAddressResponse
	handleTrackSignupStatus()
	if (!address.value?.length && fiberStatus.value) {
		// i'm not sure if this will be necessary once our links are updated for i18n
		navigateTo({ path: localePath('/get-fiber') })
	}
})
</script>
